import { toast } from "sonner";
import { getAuthToken } from '../utils/auth';
import { isOfflinePaymentRecord } from './offlinePaymentMethods';
import { createAdminEditIdempotencyKey } from './adminEditIdempotencyKey';

/**
 * Soft deletes a payment (offline payments only)
 * @param {string} transactionID - The transaction ID to soft delete
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const softDeletePayment = async (transactionID) => {
  try {
    const idempotencyKey = createAdminEditIdempotencyKey();
    // Get Clerk auth token
    const token = await getAuthToken();

    let endpoint = `${
      import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL
    }/checkout/edit-donation`;

    const response = await fetch(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify({
        transactionID: transactionID,
        softDeleted: true,
      }),
    });

    if (response.ok) {
      return true;
    } else {
      const errorData = await response.json();
      console.error(`Failed to delete payment ${transactionID}:`, errorData);
      return false;
    }
  } catch (error) {
    console.error("Error deleting payment:", error);
    return false;
  }
};

/**
 * Soft deletes multiple payments (offline payments only)
 * @param {string[]} transactionIDs - Array of transaction IDs to soft delete
 * @param {number} chunkSize - Number of concurrent requests per batch (default: 5)
 * @returns {Promise<{success: number, failed: number}>} - Returns count of successful and failed deletions
 */
export const softDeletePaymentsBulk = async (transactionIDs, chunkSize = 5) => {
  let success = 0;
  let failed = 0;

  // Show initial pending toast
  toast.loading(
    `Deleting ${transactionIDs.length} payment${transactionIDs.length !== 1 ? 's' : ''}...`,
    {
      id: 'bulk-delete-payment',
    },
  );

  for (let i = 0; i < transactionIDs.length; i += chunkSize) {
    const chunk = transactionIDs.slice(i, i + chunkSize);
    const results = await Promise.all(
      chunk.map(id => softDeletePayment(id))
    );

    success += results.filter(r => r).length;
    failed += results.filter(r => !r).length;

    // Update progress toast
    toast.loading(
      `Deleted ${Math.min(i + chunkSize, transactionIDs.length)} of ${
        transactionIDs.length
      } payment${transactionIDs.length !== 1 ? 's' : ''}...`,
      {
        id: 'bulk-delete-payment',
      },
    );

    // Small delay between chunks to avoid overwhelming the server
    if (i + chunkSize < transactionIDs.length) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // Dismiss loading toast and show final result
  toast.dismiss('bulk-delete-payment');
  if (failed === 0) {
    toast.success(`Successfully deleted ${success} payment${success !== 1 ? 's' : ''}`);
  } else if (success === 0) {
    toast.error(`Failed to delete all ${failed} payment${failed !== 1 ? 's' : ''}`);
  } else {
    toast.warning(`Deleted ${success} payment${success !== 1 ? 's' : ''}, ${failed} failed`);
  }

  return { success, failed };
};


const TICKET_RELEASE_REASON_PATTERN = /^(PAYMENT|REFUND|NONFINANCIAL):[^:]+$/;

const hasFullyReleasedTicketLedger = (payment) =>
  payment?.ticketInventoryStatus === 'RELEASED' &&
  Number.isInteger(payment?.ticketInventoryRevision) &&
  payment.ticketInventoryRevision >= 1 &&
  Array.isArray(payment?.tickets) &&
  payment.tickets.length > 0 &&
  payment.tickets.every((attendee) => {
    if (!attendee || typeof attendee !== 'object') return false;
    const reasons = attendee.ticketInventoryReleaseReasons;
    return (
      Array.isArray(reasons) &&
      reasons.length > 0 &&
      new Set(reasons).size === reasons.length &&
      reasons.every(
        (reason) => typeof reason === 'string' && TICKET_RELEASE_REASON_PATTERN.test(reason),
      )
    );
  });

export const hasActiveV2TicketInventory = (payment) =>
  payment?.ticketContractVersion === 2 && !hasFullyReleasedTicketLedger(payment);

export const canSoftDeletePayment = (payment) =>
  isOfflinePaymentRecord(payment) && !hasActiveV2TicketInventory(payment);
